import React, { useState, useEffect } from 'react';
import {
  getKinesioProfile, saveKinesioProfile,
  getGMFCSList, createGMFCS, updateGMFCS, deleteGMFCS,
  getThresholdList, createThreshold, updateThreshold, deleteThreshold,
} from '../api';
import ModalConfirmacion from '../components/ModalConfirmacion';
import useRegistrosConHistorial from '../hooks/useRegistrosConHistorial';

// ── Descripciones GMFCS ───────────────────────────────────────────────────────
const GMFCS_NIVELES = {
  children: [
    {
      nivel: 1,
      titulo: 'Nivel I',
      descripcion:
        'El niño anda en su domicilio, la escuela, por el exterior y en el vecindario. Puede subir escaleras sin apoyarse en una barandilla. El niño tiene habilidades motoras como correr y saltar, pero la velocidad, el equilibrio y la coordinación están limitados.',
    },
    {
      nivel: 2,
      titulo: 'Nivel II',
      descripcion:
        'El niño anda en la mayoría de los ámbitos, pero los factores ambientales y la elección personal influyen en sus opciones de movilidad. Puede necesitar un dispositivo manual para desplazarse con seguridad y subir escaleras sujetándose a una barandilla. En espacios exteriores puede utilizar un dispositivo con ruedas para largas distancias.',
    },
    {
      nivel: 3,
      titulo: 'Nivel III',
      descripcion:
        'El niño anda utilizando un dispositivo manual para desplazarse. Puede subir escaleras sujetándose a una barandilla con supervisión o asistencia. En la escuela puede autopropulsarse en una silla de ruedas manual o desplazarse en una motorizada. En espacios exteriores es transportado en silla de ruedas.',
    },
    {
      nivel: 4,
      titulo: 'Nivel IV',
      descripcion:
        'Para desplazarse, el niño utiliza dispositivos con ruedas en la mayoría de los ámbitos. Para trasladarlo se necesita asistencia física de 1–2 personas. En interiores puede caminar distancias cortas con asistencia física o usar un andador con soporte corporal. Puede manejar una silla de ruedas motorizada.',
    },
    {
      nivel: 5,
      titulo: 'Nivel V',
      descripcion:
        'El niño es transportado en silla de ruedas manual en todos los ámbitos. Tiene limitada su capacidad para mantener la cabeza y el tronco erguido y para controlar los movimientos de piernas y brazos. La propia movilidad está gravemente afectada, incluso utilizando tecnología de apoyo.',
    },
  ],
  adults: [
    {
      nivel: 1,
      titulo: 'Nivel I',
      descripcion:
        'Camina tanto en interiores como en exteriores y sube escaleras sin limitaciones. Realiza habilidades motoras gruesas como correr y saltar, aunque la velocidad, el equilibrio y la coordinación pueden estar reducidos.',
    },
    {
      nivel: 2,
      titulo: 'Nivel II',
      descripcion:
        'Camina en la mayoría de los entornos. Puede necesitar un dispositivo de asistencia al aprender a moverse; en exteriores o largas distancias puede requerir silla de ruedas. Los factores ambientales y la elección personal influyen en las opciones de movilidad.',
    },
    {
      nivel: 3,
      titulo: 'Nivel III',
      descripcion:
        'Camina con un dispositivo de movilidad de ayuda manual en interiores y exteriores sobre superficies llanas. Puede subir escaleras sujetándose a una barandilla. Puede autopropulsarse en silla de ruedas manual o utilizar una motorizada.',
    },
    {
      nivel: 4,
      titulo: 'Nivel IV',
      descripcion:
        'Puede mantener el nivel de función logrado antes de los 12 años o depender más de la silla de ruedas en el hogar, la escuela y la comunidad. Puede lograr autonomía de movilidad utilizando silla de ruedas motorizada.',
    },
    {
      nivel: 5,
      titulo: 'Nivel V',
      descripcion:
        'Las dificultades físicas no permiten controlar el movimiento voluntario ni mantener la cabeza y el tronco erguido contra la gravedad. Todas las áreas de la función motora están limitadas. La tecnología de adaptación no compensa completamente las limitaciones.',
    },
  ],
};

// ── Sección colapsable ────────────────────────────────────────────────────────
function Seccion({ titulo, children, defaultOpen = false }) {
  const [abierta, setAbierta] = useState(defaultOpen);
  return (
    <div className="kinesio-seccion">
      <button
        type="button"
        className={`kinesio-seccion-btn ${abierta ? 'kinesio-seccion-btn-activo' : ''}`}
        onClick={() => setAbierta(p => !p)}
      >
        <span>{titulo}</span>
        <span className="sesion-chevron">{abierta ? '▲' : '▼'}</span>
      </button>
      {abierta && <div className="kinesio-seccion-contenido">{children}</div>}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
function FichaKinesio({ pacienteId, onDataChange }) {
  // Perfil kinesiológico
  const [area, setArea]                         = useState('');
  const [firstSessionDate, setFirstSessionDate] = useState('');
  const [perfilGuardado, setPerfilGuardado]     = useState(false);
  const [perfilGuardando, setPerfilGuardando]   = useState(false);

  // GMFCS — lista de evaluaciones, modo edición por-registro via hook compartido
  const gmfcsHook = useRegistrosConHistorial([]);
  const [gmfcsSeccionAbierta, setGmfcsSeccionAbierta] = useState(false);

  // Threshold — lista de registros, modo edición por-registro via hook compartido
  const threshHook = useRegistrosConHistorial([]);

  const [error, setError] = useState('');

  // Cargar datos al montar
  useEffect(() => {
    const cargar = async () => {
      try {
        const [perfil, records, threshRecords] = await Promise.all([
          getKinesioProfile(pacienteId),
          getGMFCSList(pacienteId),
          getThresholdList(pacienteId),
        ]);
        setArea(perfil.area || '');
        setFirstSessionDate(perfil.first_session_date || '');
        gmfcsHook.setRecords(records);
        threshHook.setRecords(threshRecords);
      } catch (err) {
        setError('Error cargando datos kinesiológicos.');
      }
    };
    cargar();
  }, [pacienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expone los datos hacia arriba (exportación PDF) cada vez que cambian.
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        area: area || null,
        firstSessionDate: firstSessionDate || null,
        gmfcsRecords: gmfcsHook.records,
        threshRecords: threshHook.records,
      });
    }
  }, [area, firstSessionDate, gmfcsHook.records, threshHook.records]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Guardar perfil ──
  const guardarPerfil = async () => {
    setPerfilGuardando(true);
    try {
      await saveKinesioProfile(pacienteId, { area: area || null, first_session_date: firstSessionDate || null });
      setPerfilGuardado(true);
      setTimeout(() => setPerfilGuardado(false), 2000);
    } catch (err) { setError(err.message); }
    finally { setPerfilGuardando(false); }
  };

  const formatearFecha = (f) => { if (!f) return ''; const [y, m, d] = f.split('-'); return `${d}/${m}/${y}`; };
  const todayISO = () => new Date().toISOString().slice(0, 10);

  // ── GMFCS — múltiples evaluaciones ──
  const agregarNuevaEvaluacion = async () => {
    try {
      await gmfcsHook.agregarNuevo(
        () => createGMFCS(pacienteId, { version: 'children', level: null }),
        { version: 'children', level: null, notes: '', date: '' },
      );
    } catch (err) { setError(err.message); }
  };

  const iniciarEdicionGMFCS = (record) => {
    gmfcsHook.iniciarEdicion(record.id, {
      version: record.version || 'children',
      level: record.level || null,
      notes: record.notes || '',
      date: record.date || '',
    });
  };

  const guardarEvaluacion = async (recordId) => {
    try {
      await gmfcsHook.guardar(recordId, (edicion) => updateGMFCS(pacienteId, recordId, {
        version: edicion.version,
        level: edicion.level,
        notes: edicion.notes || null,
        date: edicion.date || null,
      }));
    } catch (err) { setError(err.message); }
  };

  const confirmarEliminarGMFCS = async () => {
    const recordId = gmfcsHook.confirmarEliminarId;
    if (!recordId) return;
    try {
      await gmfcsHook.eliminar(recordId, () => deleteGMFCS(pacienteId, recordId));
    } catch (err) { setError(err.message); }
  };

  // ── Threshold — múltiples registros ──
  const agregarNuevoThreshold = async () => {
    const hoy = todayISO();
    try {
      await threshHook.agregarNuevo(
        () => createThreshold(pacienteId, { imt_max: null, pep_max: null, date: hoy }),
        { imt_max: '', pep_max: '', date: hoy },
      );
    } catch (err) { setError(err.message); }
  };

  const iniciarEdicionThreshold = (record) => {
    threshHook.iniciarEdicion(record.id, {
      imt_max: record.imt_max != null ? String(record.imt_max) : '',
      pep_max: record.pep_max != null ? String(record.pep_max) : '',
      date: record.date || '',
    });
  };

  const guardarRegistroThreshold = async (recordId) => {
    try {
      await threshHook.guardar(recordId, (edicion) => updateThreshold(pacienteId, recordId, {
        imt_max: edicion.imt_max !== '' ? parseFloat(edicion.imt_max) : null,
        pep_max: edicion.pep_max !== '' ? parseFloat(edicion.pep_max) : null,
        date: edicion.date || null,
      }));
    } catch (err) { setError(err.message); }
  };

  const confirmarEliminarThreshold = async () => {
    const recordId = threshHook.confirmarEliminarId;
    if (!recordId) return;
    try {
      await threshHook.eliminar(recordId, () => deleteThreshold(pacienteId, recordId));
    } catch (err) { setError(err.message); }
  };

  const nivelesParaRecord = (version) => GMFCS_NIVELES[version] || GMFCS_NIVELES.children;

  return (
    <div className="kinesio-wrapper">
      <div className="kinesio-titulo-modulo">
        <span className="kinesio-badge">Kinesiología</span>
      </div>

      {error && <div className="cal-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* ── Sección 1: Perfil ── */}
      <Seccion titulo="Perfil kinesiológico" defaultOpen>
        <div className="kinesio-campo-grupo">
          <label className="kinesio-label">Área</label>
          <select className="ficha-input kinesio-select" value={area} onChange={e => setArea(e.target.value)}>
            <option value="">Sin especificar</option>
            <option value="motor">Área Motora</option>
            <option value="respiratory">Área Respiratoria</option>
            <option value="both">Ambas</option>
          </select>
        </div>
        <div className="kinesio-campo-grupo">
          <label className="kinesio-label">Fecha de primera sesión</label>
          <div className="recom-card-fecha-row">
            <input
              type="date"
              className="ficha-input date-input"
              value={firstSessionDate}
              onChange={e => setFirstSessionDate(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
            />
            <button type="button" className="recom-btn-hoy" onClick={() => setFirstSessionDate(todayISO())}>
              Hoy
            </button>
          </div>
        </div>
        <button className="btn-guardar-sesion" onClick={guardarPerfil} disabled={perfilGuardando}>
          {perfilGuardado ? 'Guardado ✓' : perfilGuardando ? 'Guardando...' : 'Guardar perfil'}
        </button>
      </Seccion>

      {/* ── Sección 2: Evaluaciones GMFCS (múltiples) ── */}
      <div className="kinesio-seccion">
        <div className="sesiones-header">
          <button type="button" className="acordeon-toggle-btn" onClick={() => setGmfcsSeccionAbierta(p => !p)}>
            <h3 className="kinesio-seccion-titulo">
              Escala GMFCS ({gmfcsHook.records.length} evaluación{gmfcsHook.records.length !== 1 ? 'es' : ''})
            </h3>
            <span className="sesion-chevron">{gmfcsSeccionAbierta ? '▲' : '▼'}</span>
          </button>
          <button type="button" className="btn-guardar-sesion sesiones-btn-nueva" onClick={agregarNuevaEvaluacion} disabled={gmfcsHook.creando}>
            {gmfcsHook.creando ? 'Creando...' : '+ Nueva evaluación GMFCS'}
          </button>
        </div>

        {gmfcsSeccionAbierta && (
        <div className="kinesio-seccion-contenido">
        {gmfcsHook.records.length === 0 && (
          <p className="sesiones-vacio" style={{ marginTop: 10 }}>
            No hay evaluaciones GMFCS registradas para este paciente.
          </p>
        )}

        {gmfcsHook.records.map((record) => {
          const abierto = !!gmfcsHook.abierto[record.id];
          const edicion = gmfcsHook.editando[record.id];
          const version = edicion ? edicion.version : (record.version || 'children');
          const level = edicion ? edicion.level : record.level;
          const niveles = nivelesParaRecord(version);

          const labelFecha = record.date ? formatearFecha(record.date) : 'Sin fecha';
          const labelNivel = record.level ? `Nivel ${record.level}` : 'Sin nivel';
          const createdAt = record.created_at
            ? new Date(record.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '';

          return (
            <div key={record.id} className="sesion-acordeon" style={{ marginTop: 10 }}>
              <button
                className={`sesion-fecha-btn ${abierto ? 'sesion-fecha-btn-activo' : ''}`}
                onClick={() => gmfcsHook.toggleAbierto(record.id)}
              >
                <span>
                  Evaluación — {labelFecha} — {labelNivel}
                  {createdAt && <span style={{ fontSize: 11, opacity: 0.65, marginLeft: 8 }}>Creada {createdAt}</span>}
                </span>
                <span className="sesion-chevron">{abierto ? '▲' : '▼'}</span>
              </button>

              {abierto && (
                <div className="sesion-contenido" style={{ padding: '12px 16px' }}>
                  {/* Botón de edición */}
                  {!edicion ? (
                    <div style={{ marginBottom: 10 }}>
                      <button className="btn-guardar-sesion" onClick={() => iniciarEdicionGMFCS(record)} style={{ marginRight: 8 }}>
                        Editar
                      </button>
                      <button className="btn-eliminar-sesion" onClick={() => gmfcsHook.pedirEliminar(record.id)}>
                        Eliminar
                      </button>
                    </div>
                  ) : null}

                  {/* Modo visualización */}
                  {!edicion && (
                    <div>
                      <div className="kinesio-campo-grupo">
                        <span className="campo-label">Versión:</span>
                        <span className="campo-valor"> {version === 'children' ? 'Niños' : 'Adultos / Más grandes'}</span>
                      </div>
                      {record.level && (
                        <div className="kinesio-campo-grupo">
                          <span className="campo-label">Nivel:</span>
                          <span className="campo-valor"> Nivel {record.level} — {niveles[record.level - 1]?.titulo}</span>
                          <div className="campo-valor" style={{ marginTop: 4 }}>
                            {niveles[record.level - 1]?.descripcion}
                          </div>
                        </div>
                      )}
                      {record.date && (
                        <div className="kinesio-campo-grupo">
                          <span className="campo-label">Fecha de evaluación:</span>
                          <span className="campo-valor"> {formatearFecha(record.date)}</span>
                        </div>
                      )}
                      {record.notes && (
                        <div className="kinesio-campo-grupo">
                          <span className="campo-label">Observaciones:</span>
                          <div className="campo-valor" style={{ marginTop: 4 }}>{record.notes}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Modo edición */}
                  {edicion && (
                    <div>
                      {/* Selector de versión */}
                      <div className="kinesio-version-tabs">
                        <button type="button"
                          className={`kinesio-version-btn ${version === 'children' ? 'kinesio-version-btn-activo' : ''}`}
                          onClick={() => gmfcsHook.actualizarEdicion(record.id, { version: 'children', level: null })}>
                          Niños
                        </button>
                        <button type="button"
                          className={`kinesio-version-btn ${version === 'adults' ? 'kinesio-version-btn-activo' : ''}`}
                          onClick={() => gmfcsHook.actualizarEdicion(record.id, { version: 'adults', level: null })}>
                          Adultos / Más grandes
                        </button>
                      </div>

                      {version === 'children' && (
                        <div className="kinesio-img-ref-wrapper">
                          <img src="/WhatsApp Image 2026-05-26 at 11.40.19.jpeg" alt="Referencia GMFCS Niños" className="kinesio-img-ref" />
                          <span className="kinesio-img-ref-caption">Referencia visual — GMFCS Niños</span>
                        </div>
                      )}

                      <div className="kinesio-niveles-grid">
                        {niveles.map(({ nivel, titulo, descripcion }) => (
                          <div key={nivel}
                            className={`kinesio-nivel-card ${level === nivel ? 'kinesio-nivel-card-activo' : ''}`}
                            onClick={() => gmfcsHook.actualizarEdicion(record.id, { level: level === nivel ? null : nivel })}>
                            <div className="kinesio-nivel-titulo">{titulo}</div>
                            <div className="kinesio-nivel-desc">{descripcion}</div>
                          </div>
                        ))}
                      </div>

                      {level && (
                        <div className="kinesio-nivel-seleccionado">
                          Nivel seleccionado: <strong>Nivel {level}</strong> — {niveles[level - 1]?.titulo}
                        </div>
                      )}

                      <div className="kinesio-campo-grupo" style={{ marginTop: 12 }}>
                        <label className="kinesio-label">Fecha de la evaluación</label>
                        <div className="recom-card-fecha-row">
                          <input type="date" className="ficha-input date-input"
                            value={edicion.date || ''}
                            onChange={e => gmfcsHook.actualizarEdicion(record.id, { date: e.target.value })}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                          <button type="button" className="recom-btn-hoy"
                            onClick={() => gmfcsHook.actualizarEdicion(record.id, { date: todayISO() })}>
                            Hoy
                          </button>
                        </div>
                      </div>

                      <div className="kinesio-campo-grupo">
                        <label className="kinesio-label">Observaciones</label>
                        <textarea className="ficha-input" rows="3"
                          value={edicion.notes || ''}
                          onChange={e => gmfcsHook.actualizarEdicion(record.id, { notes: e.target.value })}
                          placeholder="Observaciones adicionales..."
                        />
                      </div>

                      <div className="kinesio-acciones">
                        <button className="btn-guardar-sesion" onClick={() => guardarEvaluacion(record.id)} disabled={gmfcsHook.guardando[record.id]}>
                          {gmfcsHook.guardado[record.id] ? 'Guardado ✓' : gmfcsHook.guardando[record.id] ? 'Guardando...' : 'Guardar evaluación'}
                        </button>
                        <button className="btn-eliminar-sesion" onClick={() => gmfcsHook.pedirEliminar(record.id)}>
                          Eliminar
                        </button>
                        <button className="modal-btn-cancelar" style={{ marginLeft: 8 }}
                          onClick={() => gmfcsHook.cancelarEdicion(record.id, () => deleteGMFCS(pacienteId, record.id))}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
        )}
      </div>

      {/* ── Sección 3: Datos generales (Threshold, múltiples registros) ── */}
      <Seccion titulo="Datos generales — Threshold (cmH₂O)">
        <div className="kinesio-gmfcs-header">
          <button className="btn-guardar-sesion" onClick={agregarNuevoThreshold} disabled={threshHook.creando}>
            {threshHook.creando ? 'Creando...' : '+ Nuevo registro'}
          </button>
          {threshHook.records.length > 0 && (
            <span className="sesiones-vacio" style={{ marginLeft: 10 }}>
              {threshHook.records.length} registro{threshHook.records.length !== 1 ? 's' : ''} registrado{threshHook.records.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {threshHook.records.length === 0 && (
          <p className="sesiones-vacio" style={{ marginTop: 10 }}>
            No hay registros de Threshold para este paciente.
          </p>
        )}

        {threshHook.records.map((record) => {
          const abierto = !!threshHook.abierto[record.id];
          const edicion = threshHook.editando[record.id];

          const labelFecha = record.date ? formatearFecha(record.date) : 'Sin fecha';
          const createdAt = record.created_at
            ? new Date(record.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '';

          const imtWorkEdit = edicion && edicion.imt_max !== '' ? (parseFloat(edicion.imt_max) * 0.3).toFixed(1) : '';
          const pepWorkEdit = edicion && edicion.pep_max !== '' ? (parseFloat(edicion.pep_max) * 0.3).toFixed(1) : '';

          return (
            <div key={record.id} className="sesion-acordeon" style={{ marginTop: 10 }}>
              <button
                className={`sesion-fecha-btn ${abierto ? 'sesion-fecha-btn-activo' : ''}`}
                onClick={() => threshHook.toggleAbierto(record.id)}
              >
                <span>
                  Registro — {labelFecha}
                  {createdAt && <span style={{ fontSize: 11, opacity: 0.65, marginLeft: 8 }}>Creado {createdAt}</span>}
                </span>
                <span className="sesion-chevron">{abierto ? '▲' : '▼'}</span>
              </button>

              {abierto && (
                <div className="sesion-contenido" style={{ padding: '12px 16px' }}>
                  {/* Botón de edición */}
                  {!edicion ? (
                    <div style={{ marginBottom: 10 }}>
                      <button className="btn-guardar-sesion" onClick={() => iniciarEdicionThreshold(record)} style={{ marginRight: 8 }}>
                        Editar
                      </button>
                      <button className="btn-eliminar-sesion" onClick={() => threshHook.pedirEliminar(record.id)}>
                        Eliminar
                      </button>
                    </div>
                  ) : null}

                  {/* Modo visualización */}
                  {!edicion && (
                    <div>
                      {record.date && (
                        <div className="kinesio-campo-grupo">
                          <span className="kinesio-label">Fecha del registro:</span>
                          <span> {formatearFecha(record.date)}</span>
                        </div>
                      )}
                      <div className="kinesio-threshold-grid">
                        <div className="kinesio-threshold-bloque">
                          <div className="kinesio-threshold-nombre">THRESHOLD IMT</div>
                          <div className="kinesio-threshold-fila">
                            <label className="kinesio-label">Máximo (cmH₂O)</label>
                            <div className="kinesio-thresh-auto">{record.imt_max != null ? record.imt_max : '—'}</div>
                          </div>
                          <div className="kinesio-threshold-fila">
                            <label className="kinesio-label kinesio-label-auto">Trabajo al 30% (cmH₂O)</label>
                            <div className="kinesio-thresh-auto">{record.imt_work != null ? record.imt_work : '—'}</div>
                          </div>
                        </div>
                        <div className="kinesio-threshold-bloque">
                          <div className="kinesio-threshold-nombre">THRESHOLD PEP</div>
                          <div className="kinesio-threshold-fila">
                            <label className="kinesio-label">Máximo (cmH₂O)</label>
                            <div className="kinesio-thresh-auto">{record.pep_max != null ? record.pep_max : '—'}</div>
                          </div>
                          <div className="kinesio-threshold-fila">
                            <label className="kinesio-label kinesio-label-auto">Trabajo al 30% (cmH₂O)</label>
                            <div className="kinesio-thresh-auto">{record.pep_work != null ? record.pep_work : '—'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Modo edición */}
                  {edicion && (
                    <div>
                      <div className="kinesio-campo-grupo" style={{ marginTop: 4 }}>
                        <label className="kinesio-label">Fecha del registro</label>
                        <div className="recom-card-fecha-row">
                          <input type="date" className="ficha-input date-input"
                            value={edicion.date || ''}
                            onChange={e => threshHook.actualizarEdicion(record.id, { date: e.target.value })}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                          <button type="button" className="recom-btn-hoy"
                            onClick={() => threshHook.actualizarEdicion(record.id, { date: todayISO() })}>
                            Hoy
                          </button>
                        </div>
                      </div>

                      <div className="kinesio-threshold-grid">
                        <div className="kinesio-threshold-bloque">
                          <div className="kinesio-threshold-nombre">THRESHOLD IMT</div>
                          <div className="kinesio-threshold-fila">
                            <label className="kinesio-label">Máximo (cmH₂O)</label>
                            <input
                              type="number"
                              className="ficha-input kinesio-thresh-input"
                              value={edicion.imt_max}
                              onChange={e => threshHook.actualizarEdicion(record.id, { imt_max: e.target.value })}
                              placeholder="0.0"
                              min="0"
                              step="0.1"
                            />
                          </div>
                          <div className="kinesio-threshold-fila">
                            <label className="kinesio-label kinesio-label-auto">Trabajo al 30% (cmH₂O)</label>
                            <div className="kinesio-thresh-auto">{imtWorkEdit || '—'}</div>
                          </div>
                        </div>
                        <div className="kinesio-threshold-bloque">
                          <div className="kinesio-threshold-nombre">THRESHOLD PEP</div>
                          <div className="kinesio-threshold-fila">
                            <label className="kinesio-label">Máximo (cmH₂O)</label>
                            <input
                              type="number"
                              className="ficha-input kinesio-thresh-input"
                              value={edicion.pep_max}
                              onChange={e => threshHook.actualizarEdicion(record.id, { pep_max: e.target.value })}
                              placeholder="0.0"
                              min="0"
                              step="0.1"
                            />
                          </div>
                          <div className="kinesio-threshold-fila">
                            <label className="kinesio-label kinesio-label-auto">Trabajo al 30% (cmH₂O)</label>
                            <div className="kinesio-thresh-auto">{pepWorkEdit || '—'}</div>
                          </div>
                        </div>
                      </div>

                      <div className="kinesio-acciones" style={{ marginTop: 12 }}>
                        <button className="btn-guardar-sesion" onClick={() => guardarRegistroThreshold(record.id)} disabled={threshHook.guardando[record.id]}>
                          {threshHook.guardado[record.id] ? 'Guardado ✓' : threshHook.guardando[record.id] ? 'Guardando...' : 'Guardar registro'}
                        </button>
                        <button className="btn-eliminar-sesion" onClick={() => threshHook.pedirEliminar(record.id)}>
                          Eliminar
                        </button>
                        <button className="modal-btn-cancelar" style={{ marginLeft: 8 }}
                          onClick={() => threshHook.cancelarEdicion(record.id, () => deleteThreshold(pacienteId, record.id))}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Seccion>

      {/* Confirmaciones */}
      {gmfcsHook.confirmarEliminarId && (
        <ModalConfirmacion
          mensaje="¿Eliminar esta evaluación GMFCS? Esta acción no se puede deshacer."
          textoConfirmar="Eliminar evaluación"
          onConfirmar={confirmarEliminarGMFCS}
          onCancelar={gmfcsHook.cancelarPedidoEliminar}
        />
      )}
      {threshHook.confirmarEliminarId && (
        <ModalConfirmacion
          mensaje="¿Eliminar este registro de Threshold? Esta acción no se puede deshacer."
          textoConfirmar="Eliminar registro"
          onConfirmar={confirmarEliminarThreshold}
          onCancelar={threshHook.cancelarPedidoEliminar}
        />
      )}
    </div>
  );
}

export default FichaKinesio;
